/** @deprecated Use {@link sendNativeEvent} instead. */
export const messageReactNative = (type: string, body: unknown) => {
  if (window.ReactNativeWebView) {
    // send data object to React Native
    window.ReactNativeWebView.postMessage(JSON.stringify({ type, body }));
  }
};

interface NativeEvents {
  signOut: never;
  languageChanged: never;
  updateUser: never;
  trainTogetherModeActive: {
    payload: boolean;
  };
  print: {
    url?: string;
    body?: string;
    orientation?: 'portrait' | 'landscape';
  };
  navigateTo: {
    payload: string;
  };
  planLinkClicked: {
    payload: string;
  };
}

/**
 * Events that can be sent to the native app.
 *
 * @see [useNativeEventListener.ts](https://github.com/cfacorp/pathway-native/blob/main/app/hooks/useNativeEventListener.ts)
 */
export type NativeEvent = keyof NativeEvents;

/** Events mapped to `never` in {@link NativeEvents} — no second argument required. */
type EventWithoutPayload = {
  [K in NativeEvent]: [NativeEvents[K]] extends [never] ? K : never;
}[NativeEvent];

/**
 * Events whose only property is `payload` (e.g. `{ payload: boolean }`).
 * Callers pass the unwrapped value directly instead of the wrapper object.
 */
type EventWithWrappedPayload = {
  [K in NativeEvent]: [NativeEvents[K]] extends [never]
    ? never
    : keyof NativeEvents[K] extends 'payload'
      ? K
      : never;
}[NativeEvent];

/** Extracts the inner `payload` type from an {@link EventWithWrappedPayload}. */
type UnwrappedPayload<T extends EventWithWrappedPayload> =
  NativeEvents[T] extends { payload: infer P } ? P : never;

/**
 * Events with a multi-property object shape (e.g. `print`).
 * Callers pass the full object as the second argument.
 */
type EventWithComplexPayload = Exclude<
  NativeEvent,
  EventWithoutPayload | EventWithWrappedPayload
>;

/**
 * Send a native event to the native app.
 *
 * @example
 * sendNativeEvent('signOut');
 * sendNativeEvent('trainTogetherModeActive', true);
 * sendNativeEvent('navigateTo', '/home');
 * sendNativeEvent('print', { html: '<h1>Report</h1>', orientation: 'landscape' });
 *
 * @see [Native App](https://github.com/cfacorp/pathway-native)
 * @see [useNativeEventListener.ts](https://github.com/cfacorp/pathway-native/blob/main/app/hooks/useNativeEventListener.ts)
 */
export function sendNativeEvent(event: EventWithoutPayload): void;
export function sendNativeEvent<T extends EventWithWrappedPayload>(
  event: T,
  payload: UnwrappedPayload<T>,
): void;
export function sendNativeEvent<T extends EventWithComplexPayload>(
  event: T,
  payload: NativeEvents[T],
): void;
export function sendNativeEvent(event: NativeEvent, payload?: unknown): void {
  const message =
    typeof payload === 'object' && payload !== null
      ? { type: event, ...payload }
      : payload !== undefined
        ? { type: event, payload }
        : { type: event };
  window.ReactNativeWebView?.postMessage(JSON.stringify(message));
}
